import MainLayout from 'layouts/MainLayout';
import {
  Navigate,
  Outlet,
  RouteObject,
  createBrowserRouter
} from 'react-router-dom';
import MainLayoutProvider from 'providers/MainLayoutProvider';
import App from 'App';
import {
  OnlyPublicAuth,
  PermissionBasedRoute,
  RequireAuth
} from 'utils/RouteComponents';
import {
  ADMIN_LEVEL,
  ADMIN_LEVEL_FORM,
  ADMIN_LEVEL_FORM_ID,
  FEDERAL_STATE,
  FEDERAL_STATE_FORM,
  APPLICATION,
  APPLICATION_TYPE_OPTION,
  APPLICATION_TYPE_OPTION_FORM,
  APPLICATION_TYPE_OPTION_ID,
  CONFIGURATION,
  GROUP_MANAGEMENT,
  GROUP_MANAGEMENT_FORM,
  FAQ,
  FAQ_DOC_1,
  FAQ_DOC_2,
  LOGIN,
  LOGS,
  OVERVIEW,
  PRODUCT,
  PRODUCT_FORM,
  ROLE,
  ROLE_MATRIX_FORM,
  ROLE_MATRIX_FORM_ID,
  SMTP_CONFIG,
  SUBSIDY_CALCULATION_WITHOUT_INCOME,
  SUBSIDY_CALCULATION_WITH_INCOME,
  SUBSIDY_CALCULATION_WITH_INCOME_CALCULATOR,
  TOOLTIP,
  USER,
  USER_FORM,
  USER_FORM_ID,
  HEALTH_APPLICATION_CALCULATION
} from 'constant/RoutesUrls';
import SignInCard from 'pages/pages/authentication/card/SignIn';
import User from 'pages/User/User';
import UserForm from 'pages/User/UserForm/UserForm';
import Product from 'pages/Product/Product';
import ProductForm from 'pages/Product/ProductForm/ProductForm';
import { Application } from 'pages/Application/Application';
import { ApplicationTypeOption } from 'pages/ApplicationTypeOption/ApplicationTypeOption';
import { Tooltip } from 'pages/Tooltip/Tooltip';
import { Role } from 'pages/Role/Role';
import { RoleForm } from 'pages/Role/RoleForm/RoleForm';
import { ApplicationPreview } from 'pages/Application/ApplicationPreview/ApplicationPreview';
import { ApplicationTypeOptionForm } from 'pages/ApplicationTypeOption/components/ApplicationTypeOptionForm';
import { WithIncomeSubsidyCalculation } from 'pages/SubsidyCalculation/WithIncome/WithIncomeSubsidyCalculation';
import { WithoutIncomeSubsidyCalculation } from 'pages/SubsidyCalculation/WithoutIncome/WithoutIncomeSubsicidyCalculation';
import { WithIncomeCalculator } from 'pages/SubsidyCalculation/WithIncomeCalculator/WithIncomeCalculator';
import { SmtpConfig } from 'pages/SmtpConfig/SmtpConfig';
import { getCookie } from 'utils/cookieUtils';
import { ACCESS_TOKEN, modules } from 'utils/enums/misc';
import { getProfile } from './redux/userProfile/userProfileAction';
import store from './redux/store';
import { getAllConfiguration } from './redux/common/commonAction';
import Logs from 'pages/logs/Logs';
import AdminLevel from 'pages/AdminLevel/AdminLevel';
import AdminLevelForm from 'pages/AdminLevel/AdminLevelForm/AdminLevelForm';
import GroupManagement from 'pages/groupManagement/GroupManagement';
import GroupManagementForm from 'pages/groupManagement/groupManagementForm/GroupManagementForm';
import FederalState from 'pages/FederalState/FederalState';
import FederalStateForm from 'pages/FederalState/FederalStateForm/FederalStateForm';
import FaqQuestions from 'pages/About/FaqQuestions';
import FaqUserguide from 'pages/About/FaqUserguide';
import FaqAdminguide from 'pages/About/FaqAdminguide';
import HealthRefundCalculation from 'pages/SubsidyCalculation/HealthRefundCalculation/HealthRefundCalculation';

const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        loader: async () => {
          const token = getCookie(ACCESS_TOKEN);
          if (token) {
            await store.dispatch(getProfile());
            await store.dispatch(getAllConfiguration());
          }
          return null;
        },
        element: (
          <RequireAuth redirectTo={LOGIN}>
            <MainLayoutProvider>
              <MainLayout />
            </MainLayoutProvider>
          </RequireAuth>
        ),
        children: [
          {
            path: USER,
            element: (
              <PermissionBasedRoute permissionsNeeded={[modules.USERS.view]}>
                <User />
              </PermissionBasedRoute>
            )
          },
          {
            path: USER_FORM,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.USERS.create, modules.USERS.edit]}
              >
                <UserForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: USER_FORM_ID,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.USERS.create, modules.USERS.edit]}
              >
                <UserForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: PRODUCT,
            element: (
              <PermissionBasedRoute permissionsNeeded={[modules.PRODUCT.view]}>
                <Product />
              </PermissionBasedRoute>
            )
          },
          {
            path: PRODUCT_FORM,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[
                  modules.PRODUCT.create,
                  modules.PRODUCT.edit
                ]}
              >
                <ProductForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: APPLICATION,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.APPLICATION.view]}
              >
                <Application />
              </PermissionBasedRoute>
            )
          },
          {
            path: OVERVIEW,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.APPLICATION.view]}
              >
                <ApplicationPreview />
              </PermissionBasedRoute>
            )
          },
          {
            path: APPLICATION_TYPE_OPTION,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.APPLICATION_REQUEST_OPTION.view]}
              >
                <ApplicationTypeOption />
              </PermissionBasedRoute>
            )
          },
          {
            path: APPLICATION_TYPE_OPTION_ID,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[
                  modules.APPLICATION_REQUEST_OPTION.create,
                  modules.APPLICATION_REQUEST_OPTION.edit
                ]}
              >
                <ApplicationTypeOptionForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: APPLICATION_TYPE_OPTION_FORM,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[
                  modules.APPLICATION_REQUEST_OPTION.create,
                  modules.APPLICATION_REQUEST_OPTION.edit
                ]}
              >
                <ApplicationTypeOptionForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: SUBSIDY_CALCULATION_WITH_INCOME,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.CONFIGURATION.view]}
              >
                <WithIncomeSubsidyCalculation
                  application_type=""
                  disable={false}
                />
              </PermissionBasedRoute>
            )
          },
          {
            path: SUBSIDY_CALCULATION_WITHOUT_INCOME,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.CONFIGURATION.view]}
              >
                <WithoutIncomeSubsidyCalculation />
              </PermissionBasedRoute>
            )
          },
          {
            path: HEALTH_APPLICATION_CALCULATION,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.CONFIGURATION.view]}
              >
                <HealthRefundCalculation />
              </PermissionBasedRoute>
            )
          },
          {
            path: SUBSIDY_CALCULATION_WITH_INCOME_CALCULATOR,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.CONFIGURATION.view]}
              >
                <WithIncomeCalculator />
              </PermissionBasedRoute>
            )
          },
          {
            path: TOOLTIP,
            element: (
              <PermissionBasedRoute permissionsNeeded={[modules.TOOLTIP.view]}>
                <Tooltip />
              </PermissionBasedRoute>
            )
          },
          {
            path: ROLE,
            element: (
              <PermissionBasedRoute permissionsNeeded={[modules.ROLE.view]}>
                <Role />
              </PermissionBasedRoute>
            )
          },
          {
            path: ROLE_MATRIX_FORM,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.ROLE.create, modules.ROLE.edit]}
              >
                <RoleForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: ROLE_MATRIX_FORM_ID,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.ROLE.create, modules.ROLE.edit]}
              >
                <RoleForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: SMTP_CONFIG,
            element: (
              <PermissionBasedRoute permissionsNeeded={[modules.MAIL.view]}>
                <SmtpConfig />
              </PermissionBasedRoute>
            )
          },
          {
            path: CONFIGURATION,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.CONFIGURATION.view]}
              >
                <Navigate to={SUBSIDY_CALCULATION_WITH_INCOME} />
              </PermissionBasedRoute>
            )
          },
          {
            path: ADMIN_LEVEL,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.ADMIN_LEVEL.view]}
              >
                <AdminLevel />
              </PermissionBasedRoute>
            )
          },
          {
            path: ADMIN_LEVEL_FORM,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[
                  modules.ADMIN_LEVEL.create,
                  modules.ADMIN_LEVEL.edit
                ]}
              >
                <AdminLevelForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: GROUP_MANAGEMENT,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.ADMIN_LEVEL.view]}
              >
                <GroupManagement />
              </PermissionBasedRoute>
            )
          },
          {
            path: GROUP_MANAGEMENT_FORM,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[
                  modules.ADMIN_LEVEL.create,
                  modules.ADMIN_LEVEL.edit
                ]}
              >
                <GroupManagementForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: FEDERAL_STATE,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[modules.FEDERAL_STATE.view]}
              >
                <FederalState />
              </PermissionBasedRoute>
            )
          },
          {
            path: FEDERAL_STATE_FORM,
            element: (
              <PermissionBasedRoute
                permissionsNeeded={[
                  modules.FEDERAL_STATE.create,
                  modules.FEDERAL_STATE.edit
                ]}
              >
                <FederalStateForm />
              </PermissionBasedRoute>
            )
          },
          {
            path: FAQ_DOC_1,
            element: <FaqUserguide />
          },
          {
            path: FAQ_DOC_2,
            element: <FaqAdminguide />
          },
          {
            path: '*',
            element: (
              <PermissionBasedRoute permissionsNeeded={['']}>
                <Navigate to={USER} />
              </PermissionBasedRoute>
            )
          }
        ]
      },
      {
        // Standalone pages — authenticated but no navbar/sidebar
        element: (
          <RequireAuth redirectTo={LOGIN}>
            <Outlet />
          </RequireAuth>
        ),
        children: [
          {
            path: FAQ,
            element: <FaqQuestions />
          }
        ]
      },
      {
        element: (
          <OnlyPublicAuth redirectTo={USER}>
            <Outlet />
          </OnlyPublicAuth>
        ),
        children: [
          {
            path: LOGIN,
            element: <SignInCard />
          }
        ]
      },
      {
        path: '*',
        element: <Navigate to={LOGIN} />
      }
    ]
  }
];

export const router = createBrowserRouter(routes);

export default routes;
